/**
 * useSettings — Hook for loading and persisting user settings via monday Storage API.
 *
 * Settings are stored per user in the monday Storage API as:
 *   Key: "chanelle_os_settings_{userId}"
 *   Value: serialised UserSettings JSON
 *
 * Also exposes preset application (which rewrites all tab layouts).
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storageGetJSON, storageSetJSON } from '../api/client';
import { useAppStore } from '../store/appStore';
import { STORAGE_KEYS, DEFAULT_USER_SETTINGS } from '../utils/defaults';
import { getFullPreset } from '../../widget-framework/presets';
import type { UserSettings, PresetName } from '../types';

const QUERY_KEY = 'user_settings';

export function useSettings() {
  const queryClient = useQueryClient();
  const { context, settings: storeSettings, setSettings } = useAppStore();
  const userId = context?.user?.id ?? null;

  const storageKey = userId ? STORAGE_KEYS.settings(userId) : null;

  // ---- Load settings ----

  const { isLoading } = useQuery<UserSettings | null>({
    queryKey: [QUERY_KEY, userId],
    queryFn: async () => {
      if (!storageKey) return null;
      const stored = await storageGetJSON<UserSettings>(storageKey);
      const merged: UserSettings = { ...DEFAULT_USER_SETTINGS, ...stored };
      setSettings(merged);
      return merged;
    },
    enabled: Boolean(storageKey),
    staleTime: Infinity, // Settings rarely change externally
  });

  // ---- Save settings ----

  const saveSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<UserSettings>) => {
      const next: UserSettings = { ...(storeSettings ?? DEFAULT_USER_SETTINGS), ...updates };
      if (storageKey) await storageSetJSON(storageKey, next);
      setSettings(next);
      return next;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  // ---- Apply a preset ----

  const applyPreset = useCallback(
    async (preset: PresetName) => {
      if (!userId) return;
      const fullPreset = getFullPreset(preset);

      // Save each tab layout
      const layoutKeys = Object.keys(fullPreset);
      await Promise.all(
        layoutKeys.map((tabId) => {
          const key = STORAGE_KEYS.layout(userId, tabId);
          return storageSetJSON(key, fullPreset[tabId]);
        })
      );

      // Update the active preset in settings
      await saveSettingsMutation.mutateAsync({ activePreset: preset });

      // Force layout queries to refetch
      queryClient.invalidateQueries({ queryKey: ['tab_layout'] });
    },
    [userId, saveSettingsMutation, queryClient]
  );

  return {
    settings: storeSettings ?? DEFAULT_USER_SETTINGS,
    isLoading,
    saveSettings: saveSettingsMutation.mutate,
    isSaving: saveSettingsMutation.isPending,
    applyPreset,
  };
}
